import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { ButtonV2 } from 'components/common/ui';
import { FieldV2 } from 'components/common/form-controls';
import { utilitiesServices } from '@services';
import { formatDate, showError } from '@utils';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import styles from './searchVAT.style';

const validationSchema = yup.object({
  vat: yup.string().required('VAT Number is required'),
});

const handleInformationData = (data) => {
  return [
    { id: crypto.randomUUID(), label: 'ΑΦΜ', value: data?.afm },
    { id: crypto.randomUUID(), label: 'Επωνυμία', value: data?.onomasia },
    {
      id: crypto.randomUUID(),
      label: 'ΔΟΥ',
      value: data?.doy_description,
    },
    {
      id: crypto.randomUUID(),
      label: 'Έναρξη',
      value: formatDate(data?.registration_date, 'EEEE, MMMM do yyyy'),
    },
    {
      id: crypto.randomUUID(),
      label: 'Κατάσταση',
      value: data?.deactivation_flag === '1' ? 'Ενεργό' : 'Απενεργοποιημένο',
    },
    {
      id: crypto.randomUUID(),
      label: 'Τύπος',
      value:
        data?.initial_flag_description === 'ΦΠ'
          ? 'Φυσικό Πρόσωπο'
          : 'Νομικό Πρόσωπο',
    },
  ];
};

const handleAddressData = (data) => {
  return [
    {
      id: crypto.randomUUID(),

      label: 'Διεύθυνση',
      value: data?.postal_address?.trim() || '-',
    },
    {
      id: crypto.randomUUID(),

      label: 'Αριθμός',
      value: `${data?.postal_address_no?.trim() || '-'}`,
    },
    {
      id: crypto.randomUUID(),
      label: 'Περιοχή',
      value: data?.postal_area_description?.trim() || '-',
    },
    {
      id: crypto.randomUUID(),
      label: 'Τ.Κ.',
      value: data?.postal_zip_code?.trim() || '-',
    },
  ];
};

const handleActivitiesData = (data) => {
  return data?.map((activity) => {
    return {
      id: crypto.randomUUID(),
      description: activity?.description,
    };
  });
};

export const SearchVAT = () => {
  const [loading, setLoading] = useState(false);
  const [generalInformation, setGeneralInformation] = useState([]);
  const [address, setAddress] = useState([]);
  const [activities, setActivities] = useState([]);
  const { control, handleSubmit } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const onSubmit = async (formData) => {
    try {
      setLoading(true);
      const response = await utilitiesServices.getVAT({
        vat: formData.vat.trim(),
      });
      const newInformations = handleInformationData(response?.data?.result);
      const newActivities = handleActivitiesData(response?.data?.activities);
      const newAddress = handleAddressData(response?.data?.result);
      setGeneralInformation(newInformations);
      setAddress(newAddress);
      setActivities(newActivities);
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false);
    }
  };
  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={styles.wrapper}>
        <Box sx={styles.content}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'end',
              gap: '15px',
              width: '400px',
            }}
          >
            <FieldV2
              isRequired
              label="VAT Number:"
              name="vat"
              control={control}
            />
            <ButtonV2
              disabled={loading}
              type="submit"
              color="secondary"
              style={{ flexShrink: 0, marginBottom: '24px' }}
            >
              Search VAT
            </ButtonV2>
          </form>
        </Box>
      </Box>

      <Box sx={{ marginTop: '30px' }}>
        {!!generalInformation?.length && (
          <Box>
            <Typography fontSize={20} fontWeight="bold">
              General Information
            </Typography>
            <Box sx={styles.informationAndAddressWrapper}>
              <Box>
                {generalInformation?.map((information) => {
                  return (
                    <Box
                      key={information.id}
                      sx={styles.informationActivitiesContent}
                    >
                      <Typography fontWeight="bold">
                        {information?.label}:
                      </Typography>
                      <Typography>{information?.value}</Typography>
                    </Box>
                  );
                })}
              </Box>
              <Box>
                {address?.map((addressItem) => {
                  return (
                    <Box
                      key={addressItem.id}
                      sx={styles.informationActivitiesContent}
                    >
                      <Typography fontWeight="bold">
                        {addressItem?.label}:
                      </Typography>
                      <Typography>{addressItem?.value}</Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        )}
        {!!activities?.length && (
          <Box>
            <Typography fontSize={20} fontWeight="bold">
              Company activities
            </Typography>
            <Box sx={styles.activityWrapper}>
              {activities?.map((activity, index) => {
                return (
                  <Box
                    key={activity.id}
                    sx={{
                      ...styles.informationActivitiesContent,
                      backgroundColor: !(index % 2) && '#eee',
                      padding: '8px',
                    }}
                  >
                    <Typography>{activity.description}</Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};
